// src/pages/WorkoutPurchaseSuccess.tsx
// Shown after a successful workout purchase / subscription
'use client';
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckCircle, Dumbbell, Clock, ChevronDown, ChevronUp, Loader2,
  Calendar, Repeat
} from "lucide-react";
import { supabase } from "@/lib/supabase";

// ── Types ──────────────────────────────────────────────────────

interface WorkoutProduct {
  id: string;
  title: string;
  description: string | null;
  sport_type: string;
  difficulty: string;
  estimated_duration_minutes: number | null;
  program_weeks: any;
  num_weeks: number;
  product_type: string;
  billing_interval: string | null;
  partner_accounts: {
    brand_name: string | null;
    logo_url: string | null;
    accent_colour: string | null;
  } | null;
}

const ENDURANCE_SPORTS = ['running', 'cycling', 'swimming'];
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ── Session Renderers ──────────────────────────────────────────────

function StrengthExercises({ exercises }: { exercises: any[] }) {
  return (
    <div className="space-y-2">
      {exercises.map((ex, idx) => (
        <div key={idx} className="flex items-start justify-between">
          <div className="flex items-start gap-2">
            <span className="text-[#a78bfa] font-mono text-xs mt-0.5">{idx + 1}</span>
            <div>
              <span className="text-sm font-medium">{ex.name}</span>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>{ex.targetSets}x{ex.targetReps}</span>
                {ex.restSeconds && <span>{ex.restSeconds}s rest</span>}
                {ex.percentage_1rm && <span>{ex.percentage_1rm}% 1RM</span>}
              </div>
            </div>
          </div>
          {ex.isMainLift && (
            <span className="text-[10px] bg-[#a78bfa]/20 text-[#a78bfa] px-1.5 py-0.5 rounded">Main</span>
          )}
        </div>
      ))}
    </div>
  );
}

function CrossfitSession({ data }: { data: any }) {
  const fmtLabels: Record<string, string> = {
    for_time: 'FOR TIME', amrap: 'AMRAP', emom: 'EMOM', tabata: 'TABATA',
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-bold text-[#a78bfa]">{fmtLabels[data.wodFormat] || data.wodFormat}</span>
        {data.rounds && <span className="text-xs text-gray-500">{data.rounds} rds</span>}
        {data.timeCap && <span className="text-xs text-gray-500">{data.timeCap}min cap</span>}
      </div>
      <div className="space-y-1">
        {data.movements?.map((m: any, idx: number) => (
          <div key={idx} className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">{idx + 1}.</span>
            <span className="font-medium">{m.reps}</span>
            <span className="text-gray-600">{m.name}</span>
            {m.weight && <span className="text-gray-500">@ {m.weight}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function EnduranceSegments({ segments }: { segments: any[] }) {
  const intensityColors: Record<string, string> = {
    easy: 'text-green-400', moderate: 'text-yellow-400', hard: 'text-orange-400', all_out: 'text-red-400',
  };
  return (
    <div className="space-y-2">
      {segments.map((seg, sIdx) => (
        <div key={sIdx}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium">{seg.name}</span>
            {seg.repeat > 1 && <span className="text-[10px] text-gray-500">x{seg.repeat}</span>}
          </div>
          <div className="space-y-1">
            {seg.steps?.map((step: any, stIdx: number) => (
              <div key={stIdx} className="flex items-center gap-2 text-xs">
                <span className="uppercase text-gray-500 w-12">{step.type}</span>
                <span className="font-mono text-gray-600 w-12">{step.duration}</span>
                <span className={`capitalize ${intensityColors[step.intensity] || 'text-gray-500'}`}>
                  {step.intensity}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SessionCard({ session, accentColor }: { session: any; accentColor: string }) {
  const exercises = session.exercises || [];
  const isWod = Boolean(session.wodFormat || exercises[0]?.wodFormat);
  const isEndurance = ENDURANCE_SPORTS.includes(session.sport_type) && exercises.length > 0 && exercises[0]?.steps;

  return (
    <div className="bg-[#ffffff] border border-[#e2e8f0] rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <Dumbbell className="w-3.5 h-3.5" style={{ color: accentColor }} />
        <span className="text-sm font-semibold truncate">{session.name}</span>
      </div>
      {isWod ? (
        <CrossfitSession data={exercises[0] || session} />
      ) : isEndurance ? (
        <EnduranceSegments segments={exercises} />
      ) : (
        <StrengthExercises exercises={exercises} />
      )}
    </div>
  );
}

// ── Week Renderer ──────────────────────────────────────────────

function WeekView({ week, accentColor }: { week: any; accentColor: string }) {
  const [expanded, setExpanded] = useState(true);
  const days = week.days || [];
  const sessionCount = days.reduce((sum: number, d: any) => sum + (d.sessions?.length || 0), 0);

  return (
    <div className="border border-[#e2e8f0] rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-[#f8fafc] transition"
      >
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4" style={{ color: accentColor }} />
          <span className="font-semibold">Week {week.weekNumber}</span>
          <span className="text-xs text-gray-500">{sessionCount} session{sessionCount !== 1 ? 's' : ''}</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {days.map((day: any, dIdx: number) => {
              if (!day.sessions || day.sessions.length === 0) return null;
              return (
                <div key={dIdx}>
                  <div className="text-xs text-gray-500 font-medium mb-1.5 uppercase">
                    {day.dayName || DAY_NAMES[dIdx] || `Day ${dIdx + 1}`}
                  </div>
                  <div className="space-y-2">
                    {day.sessions.map((sess: any, sIdx: number) => (
                      <SessionCard key={sIdx} session={sess} accentColor={accentColor} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────

export default function WorkoutPurchaseSuccess() {
  const [searchParams] = useSearchParams();
  const shareCode = searchParams.get('code');
  const [product, setProduct] = useState<WorkoutProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [showWorkout, setShowWorkout] = useState(true);

  useEffect(() => {
    (async () => {
      if (!supabase || !shareCode) { setLoading(false); return; }
      const { data } = await supabase
        .from('partner_workout_products')
        .select('*, partner_accounts(brand_name, logo_url, accent_colour)')
        .eq('share_code', shareCode)
        .maybeSingle();
      if (data) setProduct(data);
      setLoading(false);
    })();
  }, [shareCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#ffffff] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#a78bfa] animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#ffffff] flex items-center justify-center text-[#0f172a]">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-[#a78bfa] mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Payment Successful!</h1>
          <p className="text-gray-500">Thank you for your purchase.</p>
        </div>
      </div>
    );
  }

  const brandName = product.partner_accounts?.brand_name || 'Coach';
  const accentColor = product.partner_accounts?.accent_colour || '#a78bfa';
  const logoUrl = product.partner_accounts?.logo_url;
  const weeks: any[] = Array.isArray(product.program_weeks) ? product.program_weeks : [];
  const isSubscription = product.product_type === 'subscription';
  const totalSessions = weeks.reduce(
    (sum, w) => sum + (w.days || []).reduce((ds: number, d: any) => ds + (d.sessions?.length || 0), 0), 0
  );

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* Header */}
      <header className="border-b border-[#e2e8f0] px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-[#0f172a] font-bold text-lg"
              style={{ backgroundColor: accentColor }}
            >
              {brandName.charAt(0)}
            </div>
          )}
          <span className="font-semibold text-lg">{brandName}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Success Banner */}
        <div className="text-center mb-8">
          <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: accentColor }} />
          <h1 className="text-3xl font-bold mb-2">You're In!</h1>
          <p className="text-gray-500 text-lg">
            {isSubscription
              ? "Your subscription is active. New programming will appear automatically."
              : "Your program is ready. Let's get to work."}
          </p>
        </div>

        {/* Workout Card */}
        <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl overflow-hidden">
          {/* Workout Header */}
          <div className="p-6 border-b border-[#e2e8f0]">
            <div className="flex items-start justify-between mb-2">
              <h2 className="text-2xl font-bold">{product.title}</h2>
              {isSubscription && (
                <span className="flex items-center gap-1 text-xs bg-purple-500/20 text-purple-400 px-2.5 py-1 rounded-lg">
                  <Repeat className="w-3 h-3" /> Subscription
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm bg-[#f1f5f9] px-3 py-1 rounded-lg capitalize">
                {product.sport_type}
              </span>
              <span className="text-sm bg-[#f1f5f9] px-3 py-1 rounded-lg capitalize">
                {product.difficulty}
              </span>
              {weeks.length > 0 && (
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {weeks.length} week{weeks.length !== 1 ? 's' : ''}
                </span>
              )}
              {totalSessions > 0 && (
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Dumbbell className="w-4 h-4" /> {totalSessions} session{totalSessions !== 1 ? 's' : ''}
                </span>
              )}
              {product.estimated_duration_minutes && (
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Clock className="w-4 h-4" /> {product.estimated_duration_minutes} min
                </span>
              )}
            </div>
            {product.description && (
              <p className="text-gray-500 mt-3">{product.description}</p>
            )}
          </div>

          {/* Program Content */}
          <div className="p-6">
            <button
              onClick={() => setShowWorkout(!showWorkout)}
              className="flex items-center gap-2 text-sm font-semibold mb-4"
              style={{ color: accentColor }}
            >
              <Dumbbell className="w-4 h-4" />
              {showWorkout ? 'Hide' : 'Show'} Full Program
              {showWorkout ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showWorkout && (
              <div className="space-y-4">
                {weeks.length > 0 ? (
                  weeks.map((week, wIdx) => (
                    <WeekView key={wIdx} week={week} accentColor={accentColor} />
                  ))
                ) : (
                  <p className="text-gray-500 text-sm text-center py-4">
                    No program content available yet.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>
            {isSubscription
              ? "Your subscription renews automatically. You can cancel anytime."
              : "Bookmark this page to access your program anytime."}
          </p>
          <p className="mt-1">
            Powered by <span style={{ color: accentColor }}>Input</span>
          </p>
        </div>
      </main>
    </div>
  );
}
